const position = {
    name: "position",
    path: "/position",
    title: "position",
    description: "Utilities for controlling how the browser behaves when reaching the boundary of a scrolling area.",
    value: {
        'position': {
            'static': "static",
            'fixed': "fixed",
            'absolute': "absolute",
            'relative': "relative",
            'sticky': "sticky",
        }
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="static ...">
  <p>Static parent</p>
  <div class="absolute bottom-0 left-0 ...">
    <p>Absolute child</p>
  </div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="relative ...">
  <p>Relative parent</p>
  <div class="absolute bottom-0 left-0 ...">
    <p>Absolute child</p>
  </div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="static ...">
  <!-- Static parent -->
  <div class="static ..."><p>Static child</p></div>
  <div class="inline-block ..."><p>Static sibling</p></div>
  <!-- Static parent -->
  <div class="absolute ..."><p>Absolute child</p></div>
  <div class="inline-block ..."><p>Static sibling</p></div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="relative">
  <div class="fixed top-0 right-0 left-0">Contacts</div>
  <div>
    <div>
      <img src="/img/andrew.jpg" />
      <strong>Andrew Alfred</strong>
    </div>
    <div>
      <img src="/img/debra.jpg" />
      <strong>Debra Houston</strong>
    </div>
    <!-- ... -->
  </div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div>
  <div>
    <div class="sticky top-0 ...">Sticky 1</div>
    <div>
      <div>
        <img src="/img/andrew.jpg" />
        <strong>Andrew Alfred</strong>
      </div>
      <div>
        <img src="/img/aisha.jpg" />
        <strong>Aisha Houston</strong>
      </div>
      <!-- ... -->
    </div>
  </div>
  <div>
    <div class="sticky top-0">Sticky 2</div>
    <div>
      <div>
        <img src="/img/bob.jpg" />
        <strong>Bob Alfred</strong>
      </div>
      <!-- ... -->
    </div>
  </div>
  <!-- ... -->
</div>
                `
            },
        }
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">static</code>
                  utility to position an element according to the normal flow of the document:
            </p>
            <div class="relative text-sm leading-6 font-medium">
                <div class="rounded-lg border border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-900/70">
                    <div class="static h-32 border border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-400/20">
                        <p class="text-color-purple-700 dark:text-color-sky-200">Static parent</p>
                        <div class="absolute bottom-0 left-0 rounded-lg bg-color-purple-500 p-4 text-color-white shadow-lg dark:bg-color-sky-500">
                            <p>Absolute child</p>
                        </div>
                    </div>
                </div>
            </div>
            
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">relative</code>
                  utility to position an element according to the normal flow of the document:
            </p>
            <div class="relative text-sm leading-6 font-medium">
                <div class="rounded-lg border border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-900/70">
                    <div class="relative h-32 border border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-400/20">
                        <p class="text-color-purple-700 dark:text-color-sky-200">Relative parent</p>
                        <div class="absolute bottom-0 left-0 rounded-lg bg-color-purple-500 p-4 text-color-white shadow-lg dark:bg-color-sky-500">
                            <p>Absolute child</p>
                        </div>
                    </div>
                </div>
            </div>
            
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">absolute</code>
                  utility to position an element outside of the normal flow of the document, causing neighboring elements to act as if the element doesn't exist:
            </p>
            <p class="text-sm text-color-gray-600 mb-3 dark:text-color-gray-200">With static positioning</p>
            <div class="relative text-sm leading-6 font-medium">
                <div class="relative rounded-lg border border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-900/70">
                    <p class="-mt-2 mb-2 text-color-indigo-700 dark:text-color-indigo-200">Relative parent</p>
                    <div class="static h-32 border border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-400/20">
                        <p class="text-color-indigo-700 dark:text-color-indigo-200 mb-4">Static parent</p>
                        <div class="flex gap-4">
                            <div class="bottom-0 left-0 rounded-lg bg-color-purple-500 p-4 text-color-white shadow-lg dark:bg-color-sky-500">
                                <p>Static child</p>
                            </div>
                            <div class="rounded-lg bg-color-purple-100 p-4 text-color-purple-600 shadow-lg">
                                <p>Static sibling</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

             <p class="text-sm text-color-gray-600 mb-3 mt-6 dark:text-color-gray-200">With absolute positioning</p>
            <div class="relative text-sm leading-6 font-medium">
                <div class="relative rounded-lg border border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-900/70">
                    <p class="-mt-2 mb-2 text-color-indigo-700 dark:text-color-indigo-200">Relative parent</p>
                    <div class="static h-32 border border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-400/20">
                        <p class="text-color-indigo-700 dark:text-color-indigo-200 mb-4">Static parent</p>
                        <div class="flex gap-4">
                            <div class="top-0 right-0 absolute rounded-lg bg-color-purple-500 p-4 text-color-white shadow-lg dark:bg-color-sky-500">
                                <p>Absolute child</p>
                            </div>
                            <div class="rounded-lg bg-color-purple-100 p-4 text-color-purple-600 shadow-lg">
                                <p>Static sibling</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">fixed</code>
                  utility to position an element relative to the browser window:
            </p>
            <div class="relative mx-auto  max-w-sm ">
            <div class="absolute rounded-tl-lg z-10 top-0 right-0 left-0 flex items-center bg-color-gray-50/90 px-4 py-3 text-sm font-semibold text-color-gray-900 ring-1 ring-color-gray-900/10 backdrop-blur-sm dark:bg-color-gray-700/90 dark:text-color-gray-200 dark:ring-color-black/10">Contacts</div>
            <div class="relative pt-10  flex h-72 flex-col divide-y divide-dashed divide-color-gray-200 overflow-auto rounded-xl bg-color-white shadow-lg ring-1 ring-color-black/5 dark:divide-color-gray-200/5 dark:bg-color-gray-800">
                    
            <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/66.jpg">
                        <div
                        class="flex flex-col"><strong class="text-sm font-medium text-color-gray-900 dark:text-color-gray-200">Andrew Alfred</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Technical advisor</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/9.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Debra Houston</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Analyst</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/19.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Jane White</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Director, Marketing</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/14.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Ray Flint</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Technical Advisor</span></div>
                </div>
            </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">sticky</code>
                  utility to position an element as relative until it crosses a specified threshold, then treat it as fixed until its parent is off screen:
            </p>
            <div class="relative mx-auto  shadow-lg max-w-sm h-72 overflow-auto border border-color-gray-200 dark:border-color-gray-700 rounded-xl">
            <div class="sticky top-0 z-10 top-0 right-0 left-0 flex items-center bg-color-gray-50/90 px-4 py-3 text-sm font-semibold text-color-gray-900 ring-1 ring-color-gray-900/10 backdrop-blur-sm dark:bg-color-gray-700/90 dark:text-color-gray-200 dark:ring-color-black/10 shadow-none">Sticky 1</div>
            <div class="relative flex  flex-col divide-y divide-dashed divide-color-gray-200  bg-color-white ring-1 ring-color-black/5 dark:divide-color-gray-200/5 dark:bg-color-gray-800">
                    
            <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/66.jpg">
                        <div
                        class="flex flex-col"><strong class="text-sm font-medium text-color-gray-900 dark:text-color-gray-200">Andrew Alfred</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Technical advisor</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/9.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Debra Houston</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Analyst</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/19.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Jane White</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Director, Marketing</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/14.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Ray Flint</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Technical Advisor</span></div>
                </div>
            </div>
                        <div class="sticky top-0 z-10 top-0 right-0 left-0 flex items-center bg-color-gray-50/90 px-4 py-3 text-sm font-semibold text-color-gray-900 ring-1 ring-color-gray-900/10 backdrop-blur-sm dark:bg-color-gray-700/90 dark:text-color-gray-200 dark:ring-color-black/10 shadow-none">Sticky 2</div>
            <div class="relative flex  flex-col divide-y divide-dashed divide-color-gray-200  bg-color-white ring-1 ring-color-black/5 dark:divide-color-gray-200/5 dark:bg-color-gray-800">
                    
            <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/66.jpg">
                        <div
                        class="flex flex-col"><strong class="text-sm font-medium text-color-gray-900 dark:text-color-gray-200">Andrew Alfred</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Technical advisor</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/9.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Debra Houston</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Analyst</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/19.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Jane White</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Director, Marketing</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/14.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Ray Flint</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Technical Advisor</span></div>
                </div>
            </div>
                        <div class="sticky top-0 z-10 top-0 right-0 left-0 flex items-center bg-color-gray-50/90 px-4 py-3 text-sm font-semibold text-color-gray-900 ring-1 ring-color-gray-900/10 backdrop-blur-sm dark:bg-color-gray-700/90 dark:text-color-gray-200 dark:ring-color-black/10 shadow-none">Sticky 3</div>
            <div class="relative flex  flex-col divide-y divide-dashed divide-color-gray-200  bg-color-white ring-1 ring-color-black/5 dark:divide-color-gray-200/5 dark:bg-color-gray-800">
                    
            <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/66.jpg">
                        <div
                        class="flex flex-col"><strong class="text-sm font-medium text-color-gray-900 dark:text-color-gray-200">Andrew Alfred</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Technical advisor</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/9.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Debra Houston</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Analyst</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/19.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Jane White</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Director, Marketing</span></div>
                </div>
                <div class="flex items-center gap-4 p-4"><img class="h-12 w-12 rounded-full" src="https://randomuser.me/api/portraits/men/14.jpg">
                    <div
                    class="flex flex-col"><strong class="text-sm font-medium text-gray-900 dark:text-gray-200">Ray Flint</strong><span class="text-sm font-medium text-gray-500 dark:text-gray-400">Technical Advisor</span></div>
                </div>
            </div>
            </div>
            `
        },
    ]
};

export default position;

<div className="sr-only">
            <div class="absolute dark:text-color-gray-200 rounded-tl-lg rounded-r-lg pt-10 z-10 top-0 right-0 left-0 flex items-center bg-color-gray-50/90 px-4 py-3 text-sm font-semibold text-color-gray-900 ring-1 ring-color-gray-900/10 backdrop-blur-sm dark:bg-color-gray-700/90 dark:text-color-gray-200 dark:ring-color-black/10">Contacts</div>
    <div class="relative text-sm leading-6 mt-6 font-medium shadow-none">
                <div class="rounded-lg border bg-color-purple-100 p-4 text-color-purple-600 border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-900/70">
                    <div class="static h-32 border border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-400/20">
                        <p class="text-color-purple-700 dark:text-color-sky-200">Static parent</p>
                        <div class="absolute bottom-0 left-0 rounded-lg bg-color-purple-500 p-4 text-color-white shadow-lg dark:bg-color-sky-500">
                            <p>Absolute child</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="relative rounded-lg border border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-900/70">
                    <p class="-mt-2 mb-2 text-color-indigo-700 dark:text-indigo-200">Relative parent</p>
                    <div class="static h-32 border border-color-purple-700/10 bg-color-purple-400/20 p-4 dark:border-0 dark:bg-color-sky-400/20">
                        <p class="text-color-indigo-700 dark:text-color-indigo-200">Static parent</p>
                        <div class="flex gap-4">
                            <div class="bottom-0 left-0 rounded-lg bg-color-indigo-500 p-4 text-color-white shadow-lg">
                                <p>Static child?</p>
                            </div>
                            <div class="rounded-lg bg-color-indigo-100 p-4 text-color-indigo-600 shadow-lg">
                                <p>Static sibling</p>
                            </div>
                        </div>
                    </div>
                </div>
</div>